import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  ModelSignal,
  OnDestroy,
  inject,
  input,
  model,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import {
  DEFAULT_ALLOWED_TYPES,
  FileTypes,
  NotificationService,
  isImageValid,
  provideValueAccessor,
} from '@core/shared/util';

import { AvatarComponent } from '../avatar/avatar.component';
import { DisplaySize } from '../avatar/display-size.type';
import { ValueElementDirective } from '../core/value-element';
import { ImageComponent } from '../image/image.component';

@Component({
  selector: 'mp-avatar-image-upload',
  templateUrl: './avatar-image-upload.component.html',
  styleUrl: './avatar-image-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger, AvatarComponent, ImageComponent],
  providers: [provideValueAccessor(AvatarImageUploadComponent)],
})
export class AvatarImageUploadComponent extends ValueElementDirective<File | string> implements OnDestroy {
  readonly notificationService = inject(NotificationService);

  /* Content Properties */
  readonly image: ModelSignal<string | null | undefined> = model<string | null | undefined>(null);
  readonly name: InputSignal<string | null | undefined> = input<string | null | undefined>(undefined);
  readonly alternateText: InputSignal<string | undefined> = input<string>();

  /* Formatting Properties */
  readonly size: InputSignal<DisplaySize> = input<DisplaySize>('large');

  /* Technical Properties */
  readonly isUserImage: InputSignal<boolean> = input<boolean>(false);

  protected readonly acceptedFormats: FileTypes[] = DEFAULT_ALLOWED_TYPES;
  private readonly fileSizeLimitInMB = 2;
  readonly tooltip = `Erlaubte Dateiformate: .png, .jpg, .jpeg.\nMaximale Dateigröße: ${this.fileSizeLimitInMB} MB`;

  ngOnDestroy(): void {
    this.revokeExistingImageUrl();
  }

  onFileChange(event: Event): void {
    const eventTarget: HTMLInputElement = event.target as HTMLInputElement;

    if (!eventTarget) {
      return;
    }

    const file: File | null = eventTarget.files ? eventTarget.files[0] : null;
    if (!file) {
      return;
    }

    if (!isImageValid(file, this.acceptedFormats)) {
      this.notificationService.toastDanger(
        'Das ausgewählte Bildformat wird nicht unterstützt. Erlaubt sind nur .png, .jpg und .jpeg.',
      );
      return;
    }

    if (this.isFileExceedingSizeLimit(file)) {
      this.notificationService.toastDanger(
        `Die ausgewählte Datei ist zu groß. Sie darf nicht größer als ${this.fileSizeLimitInMB} MB sein.`,
      );
      return;
    }

    this.value = file;
    this.preview(file);
  }

  clearImage(): void {
    this.revokeExistingImageUrl();

    /* Currently this needs to be null, because otherwise a change detection
     * compare with `undefined` would result in `false`.
     * The preset "empty"-value used when comparing is `null` here due to the backend
     * providing `null` for unset values. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.value = null as any;
    this.image.set(null);
  }

  handleImageLoadError(): void {
    this.revokeExistingImageUrl();
    this.image.set(null);
  }

  private isFileExceedingSizeLimit(file: File): boolean {
    return file.size > this.fileSizeLimitInMB * 1e6;
  }

  private preview(fileData: File): void {
    this.revokeExistingImageUrl();

    this.image.set(URL.createObjectURL(fileData));
  }

  private revokeExistingImageUrl(): void {
    const image: string | null | undefined = this.image();
    if (image) {
      URL.revokeObjectURL(image);
    }
  }
}
